import { type INotification } from "@src/appV2/FacilityUser/notifications/types";
import {
  type AlertType,
  type NotificationCellState,
  type SelectorOption,
  type SelectorValue,
} from "@src/appV2/redesign/WorkplaceUsers/components/NotificationPreferencesDialog/types";
import { type IAlertSetting } from "src/modules/myAccount/types";

export interface CellContext {
  alertType: AlertType;
  alertSetting?: IAlertSetting;
  notification: INotification;
  notificationKey: string | boolean;
  allDisabled: boolean;
  flagDisabled: boolean;
  fieldDisabled: boolean;
}

export function buildNotificationCellState(context: CellContext): NotificationCellState {
  const {
    alertType,
    alertSetting,
    notification,
    notificationKey,
    allDisabled,
    flagDisabled,
    fieldDisabled,
  } = context;

  if (flagDisabled || fieldDisabled) {
    return { kind: "blank" };
  }

  // Push is a per-notification on/off switch backed by alertSettings.PUSH.
  // It has no role-level notify map and no daily-batch variant.
  if (alertType === "push") {
    if (typeof notification.push !== "string") {
      return { kind: "blank" };
    }
    if (allDisabled) {
      return { kind: "uneditable", text: "Off" };
    }
    return {
      kind: "switch",
      alertType,
      isOn: Boolean(alertSetting?.enabled),
      isDisabled: false,
      notificationKey,
    };
  }

  if (!alertSetting) {
    return { kind: "blank" };
  }

  if (!alertSetting.userModifiableState) {
    return { kind: "uneditable", text: alertSetting.enabled ? "On" : "Off" };
  }

  if (allDisabled) {
    return { kind: "uneditable", text: "Off" };
  }

  let value: SelectorValue = "off";
  if (alertSetting.batch) {
    value = "daily";
  } else if (alertSetting.enabled) {
    value = "on";
  }

  const supportsDaily = Boolean(alertSetting.userModifiableBatch);

  if (!supportsDaily) {
    return {
      kind: "switch",
      alertType,
      isOn: value === "on",
      isDisabled: false,
      notificationKey,
    };
  }

  const options: SelectorOption[] = [
    { value: "on", label: "On" },
    { value: "off", label: "Off" },
    { value: "daily", label: "Daily" },
  ];

  return {
    kind: "select",
    alertType,
    value,
    options,
    isDisabled: false,
    notificationKey,
  };
}
